import { LIMITS } from "../constants";
import type { FillMark } from "../extractFill";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { FieldShell } from "./fields";
import { MinusIcon, PlusIcon } from "./icons";

interface Props {
  id: string;
  label?: string;
  "aria-label"?: string;
  error?: string;
  fillStatus?: FillMark;
  value: string;
  min?: number;
  max?: number;
  onChange: (value: string) => void;
  onBlur?: () => void;
}

export class QtyValue {
  static parse(raw: string): number | null {
    if (!raw.trim()) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  static clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
  }

  static capInput(raw: string, min: number, max: number): string {
    const n = QtyValue.parse(raw);
    if (n === null) return "";
    return String(QtyValue.clamp(n, min, max));
  }
}

export function QtyStepper({
  id,
  label,
  error,
  fillStatus,
  value,
  min = LIMITS.qty.min,
  max = LIMITS.qty.max,
  onChange,
  onBlur,
  "aria-label": ariaLabel,
}: Props) {
  const copy = useQuoteCopy();
  const current = QtyValue.parse(value);
  const empty = current === null;

  const set = (next: number) => {
    onChange(String(QtyValue.clamp(next, min, max)));
  };

  return (
    <FieldShell id={id} label={label} error={error} fillStatus={fillStatus} className="nv-qty-field">
      <div className="nv-stepper">
        <button
          type="button"
          className="nv-stepper-btn"
          aria-label={copy.t("qty.decrease")}
          disabled={empty || current <= min}
          onClick={() => {
            if (current !== null) set(current - 1);
          }}
        >
          <MinusIcon size={14} />
        </button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          value={empty ? "" : String(current)}
          onChange={(e) => onChange(QtyValue.capInput(e.target.value, min, max))}
          onBlur={onBlur}
        />
        <button
          type="button"
          className="nv-stepper-btn"
          aria-label={copy.t("qty.increase")}
          disabled={current !== null && current >= max}
          onClick={() => set(empty ? min : current + 1)}
        >
          <PlusIcon size={14} />
        </button>
      </div>
    </FieldShell>
  );
}
